import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import {
  AppButton,
  StickyHeaderNavigation,
  PhoneNumberInput,
  ChooseMessagingAppModal,
} from '@/components'
import { useProfileUpdateStore } from '@/store/profileUpdate'
import { useSendPhoneVerificationCode } from '@/domain/identity/useCases'
import { toast } from 'react-hot-toast'

interface Phone {
  number: string
  countryCode: string
  countryIsoCode: string
}

const cleanPhoneNumber = (phone: string): string => {
  return phone.replace(/\s+/g, '').replace(/[^\d+]/g, '')
}

const validatePhoneNumber = (
  phone: string,
): { isValid: boolean } => {
  const cleanPhone = cleanPhoneNumber(phone)
  const isValid = cleanPhone.length >= 7 && cleanPhone.length <= 15
  return { isValid }
}

export const AddNewPhoneNumberScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [phoneNumberWithCode, setPhoneNumberWithCode] = useState<Phone>()
  const [isChooseMessagingAppModalOpen, setIsChooseMessagingAppModalOpen] =
    useState(false)

  const {
    newPhoneNumber,
    phoneErrorMessage,
    phoneIsValid,
    newPhoneMessagingMethodCode,
    setProfileUpdateFormState,
  } = useProfileUpdateStore()

  const { mutate: sendPhoneVerificationCode, isPending: isSendingCode } =
    useSendPhoneVerificationCode({
      onSuccess: () => {
        if (phoneErrorMessage?.length) {
          resetPhoneFormState()
        }
        navigate(
          '/profile/update-profile/update-phone/new-phone-number-validate-code',
        )
      },
      onError: ({ errorMessage }) => {
        if (errorMessage) {
          toast.error(errorMessage)
        }
      },
    })

  const resetPhoneFormState = () => {
    setProfileUpdateFormState({
      phoneErrorMessage: '',
      phoneIsValid: true,
    })
  }

  const checkPhone = useCallback(
    (phone: Phone) => {
      if (phoneNumberWithCode) {
        const completePhone = cleanPhoneNumber(
          `${phone?.countryCode}${phone?.number}`,
        )

        const { isValid } = validatePhoneNumber(completePhone)

        if (!isValid) {
          setProfileUpdateFormState({
            phoneErrorMessage: t('api:errors.invalidPhone'),
            phoneIsValid: false,
            newPhoneNumber: '',
          })
        } else {
          resetPhoneFormState()
          setProfileUpdateFormState({
            newPhoneNumber: completePhone,
            phoneIsValid: true,
          })
        }
      }
    },
    [phoneNumberWithCode, setProfileUpdateFormState, t],
  )

  const debounceOnChangeTextInput = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout
      return ({ phone }: { phone: Phone }) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => {
          if (phone?.number && phone?.countryIsoCode) {
            checkPhone(phone)
          }
        }, 500)
      }
    })(),
    [checkPhone],
  )

  const handleSubmit = () => {
    sendPhoneVerificationCode({
      phone: newPhoneNumber || '',
      send_method: newPhoneMessagingMethodCode || 'sms',
    })
  }

  const handlePhoneChange = (phone: Phone) => {
    const completePhone = `${phone.countryCode}${phone.number}`.replace(
      /\s+/g,
      '',
    )

    if (phoneIsValid && completePhone !== newPhoneNumber) {
      resetPhoneFormState()
    }

    setPhoneNumberWithCode(phone)
    debounceOnChangeTextInput({ phone })
  }

  const handleMessagingMethodSelect = ({
    code,
    img_url,
  }: {
    code: string
    img_url: string
  }) => {
    setProfileUpdateFormState({
      newPhoneMessagingMethodCode: code as any,
      newPhoneMessagingMethodImgUrl: img_url,
    })

    setTimeout(handleSubmit, 200)
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  return (
    <BaseView flex={1} bgColor="background.screen" className="h-full">
      <StickyHeaderNavigation
        title={t(
          'screens:profile.profileSettings.items.editProfile.updatePhoneNumber',
        )}
        onBackPress={handleGoBack}
      />

      <BaseView
        flex={1}
        px={12}
        style={{
          overflowY: 'auto',
        }}
      >
        <BaseView gap={16}>
          <BaseText
            fontSize={14}
            fontFamily="regular"
            color="label.description"
          >
            {t('translation:editPhoneNumber.enterTheNewPhoneNumber')}
          </BaseText>

          <PhoneNumberInput
            errorMessage={phoneErrorMessage}
            withoutBorder={true}
            value={phoneNumberWithCode}
            onChange={handlePhoneChange}
          />
        </BaseView>

        <BaseView
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '24px 12px',
            backgroundColor: 'var(--background-screen)',
            borderTop: '1px solid var(--background-card-item-root)',
          }}
        >
          <AppButton
            label={t('form:actions.update')}
            onClick={() => {
              setIsChooseMessagingAppModalOpen(true)
            }}
            disabled={!phoneIsValid}
            isLoading={isSendingCode}
          />
        </BaseView>
      </BaseView>

      <ChooseMessagingAppModal
        isOpen={isChooseMessagingAppModalOpen}
        onClose={() => setIsChooseMessagingAppModalOpen(false)}
        onSelect={handleMessagingMethodSelect}
      />
    </BaseView>
  )
}

export default AddNewPhoneNumberScreen
